import React from 'react';

import { BrowserRouter as Router } from 'react-router-dom';

import { RetrieveResourcesByListAndAreaOutputDtoSingleOutputMessage } from '@api/ecommerce-xp';
import { Debug } from '@bcp-channels-sharedcomponents-reactjs/core';
import {
	CoreHelper,
	LoggerHelper,
	ShowHideHelper,
	TranslationHelper,
} from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { useDidMount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { Company, Language } from '@bcp-channels-sharedcomponents-reactjs/core/dist/types';
import { useBcpDxNextgenTouchpointFramework } from '@bcp-nextgen-dx-touchpoint-framework/react-js';
import { GLOBAL_LOADER } from '@components';
import { ApiConfig, frameworkConfigs, LoggerConfig } from '@configs';
import { TranslationValues } from '@constants';
import { getSavedTokenDataFromStorage } from '@helpers';
import { useLogger } from '@hooks';
import { BaseStateSessionInfo } from '@store';

import packageJson from '../package.json';
import { App } from './app';
import { Anything } from './types/common';

interface TouchpointFrameworkBootstrapProps {
	sessionInfo: BaseStateSessionInfo;
}

export const TouchpointFrameworkBootstrap: React.FC<TouchpointFrameworkBootstrapProps> = ({
	sessionInfo,
}): JSX.Element => {
	const baseUrl = sessionInfo.baseUrl || window.location.pathname;
	const logger = useLogger();
	const accessToken = getSavedTokenDataFromStorage('tokenOAuth');
	const language = getSavedTokenDataFromStorage('languageCode') as string;
	let languageCode = Language.pt;

	switch (language.toUpperCase()) {
		case 'ES':
			languageCode = Language.es;
			break;
		case 'EN':
			languageCode = Language.en;
			break;
		case 'FR':
			languageCode = Language.fr;
			break;
		default:
			languageCode = Language.pt;
	}
	const { FrameworkProvider } = useBcpDxNextgenTouchpointFramework({
		...frameworkConfigs,
		journeyEngine: {
			...frameworkConfigs.journeyEngine,
			navigators: frameworkConfigs.journeyEngine.navigators.map((route) => {
				return {
					...route,
				};
			}),
		},
		communicationEngine: {
			...frameworkConfigs.communicationEngine,
			defaultHeaders: {
				...frameworkConfigs.communicationEngine.defaultHeaders,
				authorization: `Bearer ${accessToken}`,
				'ocp-apim-subscription-key': ApiConfig.headers['ocp-apim-subscription-key'],
			},
		},
	});

	useDidMount(() => {
		const url =
			(process.env.REACT_APP_MOCK_START as string) === 'true'
				? (process.env.REACT_APP_API_URL as string)
				: ApiConfig.url;
		TranslationHelper.init({
			company: sessionInfo.company?.toLowerCase() === 'ab' ? Company.ab : Company.bcp,
			language: languageCode,
			siteName: TranslationValues.siteName,
			listName: TranslationValues.listName,
			keyGroup: TranslationValues.keyGroups.global,
			httpRequest: {
				url: `${url}api/rpc/RetrieveResourcesByListAndArea`,
				headers: {
					accept: ApiConfig.headers.accept,
					authorization: `Bearer ${accessToken}`,
					'ocp-apim-subscription-key': ApiConfig.headers['ocp-apim-subscription-key'],
					'content-type': ApiConfig.headers['content-type'],
				} as Anything,
				loading: {
					hide: (): void => {
						LoggerHelper.log('Translation', 'Translation > init > hideLoading');

						ShowHideHelper.hide(GLOBAL_LOADER);
					},
					show: (): void => {
						LoggerHelper.log('Translation', 'Translation > init > showLoading');

						ShowHideHelper.show(GLOBAL_LOADER);
					},
				},
				onError: (err: Anything): void => {
					LoggerHelper.log('Translation', 'Translation > init > treatResponseError', err);
					// ModalHelper.show(Modals.genericError);
				},
				onSuccess: (res: RetrieveResourcesByListAndAreaOutputDtoSingleOutputMessage): Anything => {
					return TranslationHelper.transformArrayToObject(res.data?.list || [], {
						propNameKey: 'contentID',
						propNameValue: 'content',
					});
				},
				buildBody: (input): Anything => ({
					data: {
						languageAware: languageCode.toUpperCase(),
						listName: input.listName,
						siteName: input.siteName,
						screenID: input.keyGroup,
						resourceID: '',
						useCache: true,
						accountId: 0,
						metadata: {
							alias: sessionInfo.alias,
							application: '14',
							geo: sessionInfo.geoId,
							languageCode: languageCode.toUpperCase(),
							sessionId: sessionInfo.sessionId,
							targetInfrastructure: '21', //sessionInfo.targetInfrastructure,
							targetSystem: 'PTCorporate', //sessionInfo.targetSystem,
						},
					},
					metadata: {
						messageId: '',
						messageCreatorId: '',
						messageCreationTimestampUTC: new Date(),
						correlationId: '',
						distributedTracingContext: '',
						authenticationValue: '',
						validateOnly: false,
						languageCode: languageCode.toUpperCase(),
					},
				}),
			},
		});
	});

	logger('Touchpoint bootstrap', { sessionInfo, baseUrl });

	return (
		<>
			<FrameworkProvider>
				<Router>
					<App />
				</Router>
			</FrameworkProvider>
			{LoggerConfig.debug && (
				<div className="divVersion">
					<Debug location={window.location.pathname} version={packageJson.version} />
				</div>
			)}
		</>
	);
};
